import { toLower, size } from "lodash";
import { Link } from "react-router-dom";
import { ActionButton, Button, DropdownButton } from "../../../components/Button";
import { MarkText } from "../../../components/MarkText";
import { AddPackageIcon } from "../../../icons/AddPackageIcon";
import { CircleIcon } from "../../../icons/CircleIcon";
import { VerifiedIcon } from "../../../icons/VerifiedIcon";
import { MAX_SELECTED, MINOS, SWTYPES, SYSARCH, YESNO } from "../../../settings";
import { formatDate, getTitle, highlightText, isNone } from "../../../utils";
import { Badge } from "../../../components/Circle";
import { DownloadIcon } from "../../../icons/DownloadIcon";
import { Flex } from "../../../components/Flex";
import { Paragraph } from "../../../components/Text";
import { CloseIcon } from "../../../icons/CloseIcon";
import { CircleCheckIcon } from "../../../icons/CircleCheckIcon";
import { KitIcon } from "../../../icons/KitIcon";
import { MoreIcon } from "../../../icons/MoreIcon";
import { ArrowBackIcon } from "../../../icons/ArrowBackIcon";


export const packages = ({ isAdmin, adminRead, packageRead, packageWrite, context, pathname, search, filterOptions={}, showForm, selectedPackages, selectItem, setSelection, downloadPackages, products =[] }) => ({
    enabled: packageRead,
    header: isAdmin
        ? <DropdownButton
            items={[
                { key: 0, title: "Packages report" },
                { key: 1, title: <Link to={{ pathname: `${pathname}/masterkit`, state: { from: pathname + search }}} title="Master Kit list">View Master Kit list</Link>}
            ]}
            onMenuClick={({ key }) => {
                if (key == 0) {
                    showForm("packages_report");
                }
            }}
        >
           <ActionButton><MoreIcon /></ActionButton>
        </DropdownButton>
        : <Link to={{ pathname: `${pathname}/masterkit`, state: { from: pathname + search }}} >
            <ActionButton title="Master Kit list">
                <KitIcon />
            </ActionButton>
        </Link>,
    footer: <>
        {packageWrite && <Button onClick={() => showForm("packages")}>
            <AddPackageIcon size={20} />
            Register
        </Button>}
        {selectedPackages == null
            ? <Button title={`Select packages for download${isAdmin ? " or mass updating" : ""}.`} onClick={() => setSelection()}>
                <CircleCheckIcon size={20} />
                Select
            </Button>
            : <>
                {isAdmin
                    ? <DropdownButton
                        items={[
                            { key: 0, title: "Download packages", disabled: !size(selectedPackages) },
                            { key: 1, title: <Link to={{ pathname: `${pathname}/edit`, search: `?ids=${Object.keys(selectedPackages).join(",")}`, state: { from: pathname + search }}}>Edit packages</Link>, disabled: !size(selectedPackages)}
                        ]}
                        onMenuClick={({ key }) => {
                            if (key == 0) {
                                downloadPackages()
                            }
                        }}
                    >
                        <Button>
                            <ArrowBackIcon size={20} rotate={90} />
                            Select
                        </Button>
                    </DropdownButton>
                    : <Button disabled={!size(selectedPackages)} onClick={() => downloadPackages()}>
                        <DownloadIcon size={18} />
                        Download
                    </Button>}
                <Flex align="center">
                    <Paragraph bold color="var(--sp-label-color)">{size(selectedPackages)} package{size(selectedPackages) != 1 && "s"} selected</Paragraph>
                    <CloseIcon size={20} color="var(--sp-error-color)" hoverColor="var(--sp-error-hover-color)" onClick={() => setSelection()} />
                </Flex>
            </>
        }
    </>,
    columns: [
        {
            title: <Flex flex={1}><CircleIcon size={selectedPackages == null ? 16 : 20} /></Flex>,
            dataIndex: selectedPackages == null ? 'verified' : 'selected',
            render: (text, record, index) =>
                selectedPackages === null
                    ? <Badge size={8} color={text == 1 ? "var(--sp-success-color)" : "var(--sp-error-color)"}/>
                    : <Flex flex={1} style={{ cursor: "pointer"}} onClick={() => record.size > 0 && (size(selectedPackages) < MAX_SELECTED || selectedPackages[record.key] || isAdmin) ? selectItem(record) : null}>{selectedPackages[record.key] ? <VerifiedIcon size={18} /> : <CircleIcon size={20}/>}</Flex>,
            width: 56,
            sorter: selectedPackages === null && adminRead ? (a, b) => { if (a && a.hasOwnProperty("verified") && a.verified) return a.verified - b.verified } : null
        },
        {
            title: "Package Name",
            dataIndex: "name",
            defaultSortOrder: "asc",
            render: (text, record, index) => (
                <Link to={{ pathname: `${pathname}/${record.key}`, state: { from: pathname + search }}} style={{ opacity: selectedPackages == null || record.size > 0 && (size(selectedPackages) < MAX_SELECTED || selectedPackages[record.key] || isAdmin) ? 1 : 0.5, color: record.downloaded ? "var(--sp-success-color)" : undefined }}>
                    <MarkText nowrap title={text + (record.archived ? " - archived" : "")} style={{ fontWeight: 600 }} text={highlightText(text, context)} />
                </Link>
            ),
            sorter: (a, b) => { if (a && a.hasOwnProperty("name") && a.name) return a.name.localeCompare(b.name) }
        },
        {
            title: "Version",
            dataIndex: "version",
            width: 100,
            render: (text, record, index) => (
                <>
                    <MarkText title={text} text={highlightText(text, context)} />
                    {record.archived ? <sup><Badge size={6} color="var(--sp-text-grey-color)" /></sup> : null}
                </>
            ),
            sorter: (a, b) => { if (a && a.hasOwnProperty("version") && a.version) return a.version.localeCompare(b.version) }
        },
        {
            title: "Release date",
            dataIndex: "releaseDate",
            width: 180,
            render: (text, record, index) => (
                <MarkText title={text} text={isNone(text) ? text : formatDate(text, "DD-MMM-YYYY")} />
            ),
            sorter: (a, b) => { if (a && a.hasOwnProperty("releaseDate") && a.releaseDate) return a.releaseDate.localeCompare(b.releaseDate) }
        },
        {
            title: "Arch",
            dataIndex: 'sysarch',
            width: 100,
            render: (text, record, index) => (
                <MarkText title={text} text={highlightText(text, context)} />
            ),
            sorter: (a, b) => { if (a && a.hasOwnProperty("sysarch") && a.sysarch) return a.sysarch.localeCompare(b.sysarch) }
        },
        {
            title: "OS Version",
            dataIndex: 'minos',
            width: 150,
            render: (text, record, index) => (
                <MarkText nowrap title={text} text={highlightText(getTitle(text, MINOS), context)} />
            ),
            sorter: (a, b) => { if (a && a.hasOwnProperty("minos") && a.minos) return a.minos.localeCompare(b.minos) }
        },
        {
            title: "Kit Type",
            dataIndex: "swtype",
            width: 150,
            render: (text, record, index) => (
                <MarkText title={text} text={highlightText(text, context)} />
            ),
            sorter: (a, b) => { if (a && a.hasOwnProperty("swtype") && a.swtype) return a.swtype.localeCompare(b.swtype) }
        },
        {
            title: "Restart",
            dataIndex: "restartRequired",
            width: 80,
            render: (text, record, index) => (
                <span>
                    {text == 1 ? "Yes" : text == 0 ? "No" : text}
                </span>
            ),
            sorter: (a, b) => { if (a && a.hasOwnProperty("restartRequired") && a.restartRequired) return a.restartRequired - b.restartRequired}
        }
    ],
    searchOptions: [
        {
            key: "sysarch",
            title: "System Architecture",
            type: "select",
            options: SYSARCH,
            placeholder: "System architecture that the package is compatible with.",
            multiple: true
        },
        {
            key: "minos",
            title: "OS Version",
            type: "select",
            options: Array.isArray(filterOptions.sysarch) && filterOptions.sysarch.length
                ? MINOS.filter(os => os.sysarch.filter(sysarch => filterOptions.sysarch.includes(toLower(sysarch))).length)
                : MINOS,
            placeholder: "OS version that the package can be installed on.",
            multiple: true
        },
        {
            key: "swtype",
            title: "Kit type",
            type: "select",
            options: SWTYPES,
            placeholder: "Kit type of the package.",
            multiple: true
        },
        {
            key: "product",
            title: "Product",
            type: "select",
            options: products,
            multiple: true
        },
        isAdmin
            ? {
                key: "verified",
                title: "Verified",
                type: "boolean",
                options: YESNO,
                placeholder: "Package verification status (verified or not)."
            }
            : {},
        {
            key: "archived",
            title: "Show archived versions",
            type: "checkbox",
            default: false,
            options: YESNO
        }
    ],
})